/**
 * AtlasMarketsTape — live crypto · commodity · FX tape for the Atlas terminal.
 *
 * Polls `/api/internal/services/svc.atlas_markets/fetch` every 60s. Renders
 * three columns (crypto / commodity / FX) in a Bloomberg-style cell grid
 * with up/down arrows + coloured percentage change.
 *
 * Data shape per event:
 *   { title, summary, tags: [kind, symbol], metrics: { price, change_pct_24h } }
 */
import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../lib/api';

const TILE_KIND = {
  crypto:    { label: 'CRYPTO',      accent: '#f7c94b' },
  commodity: { label: 'COMMODITIES', accent: '#e8c878' },
  fx:        { label: 'FX',          accent: '#7ecfc0' },
};

function ChangePill({ pct }) {
  if (pct === null || pct === undefined) {
    return <span style={{ color: 'rgba(200,210,230,0.45)', fontSize: 10 }}>—</span>;
  }
  const up = pct >= 0;
  const color = up ? '#4ade80' : '#f87171';
  return (
    <span style={{ color, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
      {up ? '▲' : '▼'}&nbsp;{Math.abs(pct).toFixed(2)}%
    </span>
  );
}

function Row({ quote }) {
  const price = quote.metrics?.price;
  const pct = quote.metrics?.change_pct_24h;
  const priceStr = price == null
    ? '—'
    : price < 1
      ? `$${price.toFixed(4)}`
      : `$${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return (
    <div
      className="atlas-markets-row"
      data-testid={`atlas-markets-row-${quote.tags?.[1] || quote.title}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 8,
        padding: '6px 10px',
        borderBottom: '1px solid rgba(90, 110, 140, 0.08)',
        alignItems: 'center',
      }}
    >
      <span style={{ color: 'rgba(220,230,245,0.88)', fontSize: 12, letterSpacing: '0.04em' }}>
        {quote.title}
      </span>
      <span style={{ color: '#e8e8e8', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {priceStr}
      </span>
      <ChangePill pct={pct} />
    </div>
  );
}

export default function AtlasMarketsTape({ pollInterval = 60000, testId = 'atlas-markets-tape' }) {
  const [quotes, setQuotes] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.post('/api/internal/services/svc.atlas_markets/fetch', { query: {} });
        if (cancelled) return;
        const d = r.data;
        if (!d.ok) {
          setError(d.error || 'unknown error');
          return;
        }
        setError(null);
        setQuotes(d.data || []);
        setLastUpdate(new Date());
      } catch (err) {
        if (!cancelled) setError(err.message || 'fetch failed');
      }
    };
    load();
    const id = setInterval(load, pollInterval);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollInterval]);

  const grouped = useMemo(() => {
    const buckets = { crypto: [], commodity: [], fx: [] };
    (quotes || []).forEach((q) => {
      const kind = q.tags?.[0];
      if (buckets[kind]) buckets[kind].push(q);
    });
    return buckets;
  }, [quotes]);

  return (
    <div
      data-testid={testId}
      className="atlas-markets-tape"
      style={{
        border: '1px solid rgba(120, 150, 200, 0.18)',
        background: 'rgba(5, 7, 11, 0.55)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          borderBottom: '1px solid rgba(120, 150, 200, 0.18)',
          background: 'rgba(10, 14, 22, 0.55)',
        }}
      >
        {(['crypto', 'commodity', 'fx']).map((k) => (
          <div
            key={k}
            style={{
              padding: '6px 10px',
              fontSize: 9,
              letterSpacing: '0.28em',
              color: TILE_KIND[k].accent,
              borderRight: '1px solid rgba(120, 150, 200, 0.08)',
            }}
          >
            {TILE_KIND[k].label}
            <span style={{ float: 'right', color: 'rgba(200,210,230,0.4)' }}>
              {grouped[k].length}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          minHeight: 180,
        }}
      >
        {(['crypto', 'commodity', 'fx']).map((k) => (
          <div
            key={k}
            style={{ borderRight: '1px solid rgba(120, 150, 200, 0.08)' }}
            data-testid={`atlas-markets-col-${k}`}
          >
            {grouped[k].length === 0 ? (
              <div style={{ padding: 12, fontSize: 11, color: 'rgba(200,210,230,0.35)' }}>
                {error ? 'feed offline' : 'waiting…'}
              </div>
            ) : (
              grouped[k].map((q) => <Row key={q.id} quote={q} />)
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '4px 10px',
          fontSize: 9,
          letterSpacing: '0.18em',
          color: 'rgba(200,210,230,0.38)',
          borderTop: '1px solid rgba(120, 150, 200, 0.08)',
          background: 'rgba(0,0,0,0.25)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>SRC · KRAKEN · FRANKFURTER</span>
        <span>{lastUpdate ? `UPDATED ${lastUpdate.toLocaleTimeString()}` : 'LOADING'}</span>
      </div>
    </div>
  );
}
